import { CloudOff, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactElement } from 'react';
import { Button } from '@/components/Button';
import { Notice } from '@/components/Notice';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';

export interface OfflineBoardNoticeProps {
  readonly mode: 'cached' | 'pending-sync' | 'not-available';
  readonly pendingCount?: number | undefined;
  readonly cachedAt?: string | undefined;
  readonly boardAddress?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
}

function cachedBody(t: LocaleFormattingTranslate, cachedAt: string | undefined): string {
  if (cachedAt === undefined || cachedAt.trim() === '') {
    return t('boardDashboard.offline.cachedBody');
  }
  return t('boardDashboard.offline.cachedBodyAt', { time: cachedAt });
}

function pendingSyncBody(
  t: LocaleFormattingTranslate,
  pendingCount: number | undefined,
): string {
  const count = Math.max(0, pendingCount ?? 0);
  if (count === 0) {
    return t('boardDashboard.offline.pendingBodyNone');
  }
  return t('boardDashboard.offline.pendingBody', { count }, count);
}

function retryAction(
  label: string,
  onRetry: (() => void) | undefined,
): ReactElement | null {
  if (onRetry === undefined) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      iconStart={<RefreshCw className="size-3.5" />}
      onClick={onRetry}
    >
      {label}
    </Button>
  );
}

export function OfflineBoardNotice({
  mode,
  pendingCount,
  cachedAt,
  boardAddress,
  onRetry,
}: OfflineBoardNoticeProps): ReactElement {
  const { t } = useInterfaceLanguage();

  if (mode === 'not-available') {
    const address =
      boardAddress === undefined || boardAddress.trim() === ''
        ? t('boardDashboard.offline.thisBoard')
        : boardAddress;
    return (
      <Notice
        tone="warn"
        icon={<WifiOff className="size-4" />}
        title={t('boardDashboard.offline.unavailableTitle')}
        body={t('boardDashboard.offline.unavailableBody', { address })}
        action={retryAction(t('boardDashboard.offline.retry'), onRetry)}
      />
    );
  }

  if (mode === 'pending-sync') {
    return (
      <Notice
        tone="info"
        icon={<CloudOff className="size-4" />}
        title={t('boardDashboard.offline.pendingTitle')}
        body={pendingSyncBody(t, pendingCount)}
        action={retryAction(t('boardDashboard.offline.retrySync'), onRetry)}
      />
    );
  }

  return (
    <Notice
      tone="info"
      icon={<CloudOff className="size-4" />}
      title={t('boardDashboard.offline.cachedTitle')}
      body={cachedBody(t, cachedAt)}
      action={retryAction(t('boardDashboard.offline.retryConnection'), onRetry)}
    />
  );
}
